import { formatCustomerCreationSource } from "@shared/customerCreationSource";
import { formatCustomerSearchAddress } from "@shared/customerSearchIdentity";
import type { CustomerSearchFullHit } from "@shared/customerSearchAccess";

export type CustomerSearchResultModel = CustomerSearchFullHit;

function formatCreatedOn(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
}

export function CustomerSearchResult({
  customer,
  queryMatchedAlias,
}: {
  customer: CustomerSearchResultModel;
  queryMatchedAlias?: string | null;
}) {
  const address =
    customer.formattedAddress ||
    formatCustomerSearchAddress({
      address: customer.address,
      unitNumber: customer.unitNumber,
      postalCode: customer.postalCode,
    });
  const alias = queryMatchedAlias ?? customer.matchedAlias;
  const createdOn = formatCreatedOn(customer.createdAt);
  return (
    <div className="min-w-0 text-left">
      <p className="font-medium text-[#1A2744]">
        {customer.name}
        {customer.isTest ? (
          <span
            className="ml-2 inline-flex items-center rounded bg-gray-200 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-gray-600"
            title="Internal test customer: skipped by reminders, Cleo and sales figures"
          >
            Test
          </span>
        ) : null}
      </p>
      {address ? <p className="text-xs text-muted-foreground">{address}</p> : null}
      {(customer.contact || customer.email) && (
        <p className="text-xs text-muted-foreground">
          {[customer.contact, customer.email].filter(Boolean).join(" · ")}
        </p>
      )}
      {alias && alias.trim().toLowerCase() !== customer.name.trim().toLowerCase() ? (
        <p className="text-xs text-amber-800">Previously recorded as: {alias}</p>
      ) : null}
      {(customer.createdByUserName || createdOn || customer.creationSource) && (
        <p className="text-[11px] text-muted-foreground">
          Created by {customer.createdByUserName || "Unknown"}
          {createdOn ? ` on ${createdOn}` : ""}
          {customer.creationSource ? ` · Source: ${formatCustomerCreationSource(customer.creationSource)}` : ""}
        </p>
      )}
    </div>
  );
}
